import React, { useCallback, useMemo } from "react";
import { FlatList, Platform, RefreshControl } from "react-native";
import { XStack, YStack } from "tamagui";
import { BodyText, ContentTitle } from "@/components/ui/Typography";

import { LessonCard } from "@/components/lesson/LessonCard";
import { LessonListRow } from "@/components/lesson/LessonListRow";
import { DASHBOARD_GRID_GAP } from "@/lib/dashboardToolbarUi";
import { chunkIntoRows } from "@/lib/gridRows";
import { CONTENT_MAX_WIDTH, useLessonCardLayout, useStableWindowWidth } from "@/lib/useDeviceType";
import { Lesson, UserLesson } from "@/types";

type LessonCardLayout = ReturnType<typeof useLessonCardLayout>;

const GRID_GAP = DASHBOARD_GRID_GAP;
const GRID_ROW_GAP = 32;
const CONTENT_PAD_X = 12;

type LessonsGridListProps = {
  lessons: Lesson[];
  viewMode: "grid" | "list";
  isMobile: boolean;
  searchQuery?: string;
  userLessonsMap: Record<number, UserLesson>;
  onPressLesson: (lesson: Lesson) => void;
  /** When set, cards/rows show a ✕ (e.g. remove from Continue watching). */
  onRemoveLesson?: (lesson: Lesson) => void;
  hidePublishedDate?: boolean;
  showCourseColor?: boolean;
  lessonCardLayout?: LessonCardLayout;
  /** Rendered above the items as the scrollable header (hero, search bar, etc.). */
  ListHeaderComponent?: React.ComponentProps<typeof FlatList>["ListHeaderComponent"];
  /** Rendered after the items (footer). */
  ListFooterComponent?: React.ComponentProps<typeof FlatList>["ListFooterComponent"];
  refreshing?: boolean;
  onRefresh?: () => void;
};

export function LessonsGridList({
  lessons,
  viewMode,
  isMobile,
  searchQuery,
  userLessonsMap,
  onPressLesson,
  onRemoveLesson,
  hidePublishedDate = false,
  showCourseColor = false,
  lessonCardLayout: layoutFromParent,
  ListHeaderComponent,
  ListFooterComponent,
  refreshing,
  onRefresh,
}: LessonsGridListProps) {
  const ownLayout = useLessonCardLayout();
  const lessonCardLayout = layoutFromParent ?? ownLayout;
  const { cardWidth } = lessonCardLayout;

  const windowWidth = useStableWindowWidth();
  const innerWidth = Math.min(CONTENT_MAX_WIDTH, windowWidth) - CONTENT_PAD_X * 2;

  const numColumns =
    viewMode === "list"
      ? 1
      : Math.max(1, Math.floor((innerWidth + GRID_GAP) / (cardWidth + GRID_GAP)));

  // Stretch cards to fill the row exactly (like the courses page) so gaps stay a
  // constant 16px and full rows fill edge-to-edge, while a partial last row keeps
  // a left-aligned (flex-start) layout instead of centering.
  const fillCardWidth =
    viewMode === "list"
      ? cardWidth
      : numColumns > 1
        ? Math.floor((innerWidth - GRID_GAP * (numColumns - 1)) / numColumns)
        : Math.min(cardWidth, innerWidth);

  // Pre-chunk into rows so the FlatList always uses a single column. This keeps
  // `numColumns` constant across grid/list (no remount/blink on toggle) and lets
  // the last partial row align to the start instead of centering.
  const rows = useMemo<Lesson[][]>(
    () => chunkIntoRows(lessons, viewMode === "list" ? 1 : numColumns),
    [viewMode, lessons, numColumns],
  );

  const renderItem = useCallback(
    ({ item: row }: { item: Lesson[] }) => {
      if (viewMode === "list") {
        const lesson = row[0];
        return (
          <YStack
            w={innerWidth}
            maw={CONTENT_MAX_WIDTH - CONTENT_PAD_X * 2}
            als="center"
          >
            <LessonListRow
              item={lesson}
              onPress={() => onPressLesson(lesson)}
              onRemove={
                onRemoveLesson ? () => onRemoveLesson(lesson) : undefined
              }
              userLesson={userLessonsMap[lesson.id]}
              isMobile={isMobile}
              hidePublishedDate={hidePublishedDate}
              showCourseColor={showCourseColor}
            />
          </YStack>
        );
      }
      return (
        <XStack
          w={innerWidth}
          maw={CONTENT_MAX_WIDTH - CONTENT_PAD_X * 2}
          als="center"
          gap={GRID_GAP}
          jc="flex-start"
        >
          {row.map((lesson) => (
            <YStack
              key={lesson.id}
              w={fillCardWidth}
              style={{ flexGrow: 0, flexShrink: 0 }}
            >
              <LessonCard
                item={lesson}
                userLesson={userLessonsMap[lesson.id]}
                onPress={() => onPressLesson(lesson)}
                onRemove={
                  onRemoveLesson ? () => onRemoveLesson(lesson) : undefined
                }
                {...lessonCardLayout}
                cardWidth={fillCardWidth}
              />
            </YStack>
          ))}
        </XStack>
      );
    },
    [
      viewMode,
      fillCardWidth,
      innerWidth,
      lessonCardLayout,
      userLessonsMap,
      onPressLesson,
      onRemoveLesson,
      isMobile,
      hidePublishedDate,
      showCourseColor,
    ],
  );

  const keyExtractor = useCallback(
    (row: Lesson[], index: number) =>
      `${viewMode}-${row[0]?.id ?? "row"}-${index}`,
    [viewMode],
  );

  const ItemSeparator = useCallback(() => <YStack h={GRID_ROW_GAP} />, []);

  const emptyComponent = useMemo(
    () => (
      <YStack ai="center" jc="center" py="$8" gap="$2">
        <ContentTitle
          color="#999"
          fontWeight="500"
          ta="center"
        >
          {searchQuery ? "No lessons found" : "No lessons available"}
        </ContentTitle>
        <BodyText color="#BBB" ta="center">
          {searchQuery ? "Try adjusting your search" : "This collection is empty"}
        </BodyText>
      </YStack>
    ),
    [searchQuery],
  );

  return (
    <FlatList
      style={{ flex: 1, width: "100%" }}
      data={rows}
      keyExtractor={keyExtractor}
      renderItem={renderItem}
      extraData={userLessonsMap}
      ListHeaderComponent={ListHeaderComponent}
      ListFooterComponent={ListFooterComponent}
      ListEmptyComponent={emptyComponent}
      ItemSeparatorComponent={ItemSeparator}
      contentContainerStyle={{
        paddingBottom: 16,
        flexGrow: 1,
      }}
      showsVerticalScrollIndicator={Platform.OS === "web"}
      removeClippedSubviews={Platform.OS !== "web"}
      initialNumToRender={8}
      maxToRenderPerBatch={8}
      windowSize={5}
      refreshControl={
        Platform.OS !== "web" && onRefresh ? (
          <RefreshControl refreshing={!!refreshing} onRefresh={onRefresh} />
        ) : undefined
      }
    />
  );
}
